import { useEffect, useMemo, useRef } from "react";
import { useExtend } from "@focus/focusUI/lib/services/timeExtend";
import {
  Ellipsoid,
  getCameraBPPointAndPlaneEntity,
  getCDMObjectAttributes,
  getRelativeVelocityPositions,
  Label,
  Orbit,
  rgbaToColor,
  useCesium,
  Vector,
  vfColour2RGB,
  Visual3DWidget,
} from "@focus/visual3D";
import type { CDMObject, IConjunction } from "@focus/visual3D";
import type { Entity } from "cesium";
import { Color, JulianDate } from "cesium";
import { getDisplayValue, getNestedValue } from "./conjunctionUiUtils";
import { getObjectRoleLabel } from "../../utils/objectRoleLabels";

interface ConjunctionFallback3DViewProps {
  conjunction: unknown;
}

type ObjectKey = "target" | "chaser";

const DEFAULT_DATE = "1970-01-01T00:00:00.000Z";
const VISUAL3D_COVARIANCE_KM2_SCALE = 1_000;

const getRawValue = (element: unknown, path: string[]) => {
  const raw = getNestedValue(element, path);

  if (raw && typeof raw === "object" && "value" in raw) {
    return (raw as { value?: unknown }).value;
  }

  return raw;
};

const getNumericValue = (element: unknown, path: string[]) => {
  const rawValue = getRawValue(element, path);
  const numericValue =
    typeof rawValue === "number" ? rawValue : Number.parseFloat(`${rawValue}`);

  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const getNumberOrZero = (element: unknown, path: string[]) =>
  getNumericValue(element, path) ?? 0;

const getStringValue = (
  element: unknown,
  path: string[],
  fallback = "-",
) => {
  const value = getDisplayValue(getRawValue(element, path));

  return value === "-" ? fallback : value;
};

const getPositiveNumber = (element: unknown, paths: string[][]) =>
  paths
    .map((path) => getNumericValue(element, path))
    .find((value): value is number => value !== undefined && value > 0) ?? 0;

const toIsoDate = (value: unknown) => {
  const rawDate = getDisplayValue(value);
  if (rawDate === "-") return DEFAULT_DATE;

  const normalizedDate = rawDate.replace(
    /^(\d{4})\/(\d{2})\/(\d{2})-(.+)$/,
    "$1-$2-$3T$4Z",
  );
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedDate);
  const isoCandidate = hasTimezone ? normalizedDate : `${normalizedDate}Z`;
  const parsedDate = new Date(isoCandidate);

  return Number.isNaN(parsedDate.getTime())
    ? DEFAULT_DATE
    : parsedDate.toISOString();
};

const getObjectName = (
  conjunction: unknown,
  objectKey: ObjectKey,
  fallback: string,
) => {
  const objectName = getStringValue(
    conjunction,
    [objectKey, "spaceObject", "name"],
    "",
  );
  const shortName = getStringValue(conjunction, [`${objectKey}Name`], "");

  return objectName || shortName || fallback;
};

const getObjectDesignator = (conjunction: unknown, objectKey: ObjectKey) => {
  const noradId = getRawValue(conjunction, [
    objectKey,
    "spaceObject",
    "noradId",
  ]);
  const cosparId = getRawValue(conjunction, [
    objectKey,
    "spaceObject",
    "cosparId",
  ]);

  return (noradId as string | number | undefined) ?? getDisplayValue(cosparId);
};

const getObjectSource = (conjunction: unknown, objectKey: ObjectKey) =>
  getStringValue(conjunction, [`${objectKey}Source`], "OPS");

const getCovarianceValue = (conjunction: unknown, path: string[]) =>
  getNumberOrZero(conjunction, path) * VISUAL3D_COVARIANCE_KM2_SCALE;

const getCovarianceMatrix = (conjunction: unknown, objectKey: ObjectKey) => ({
  cnN: getCovarianceValue(conjunction, [`${objectKey}CovNN`]),
  cnR: getCovarianceValue(conjunction, [`${objectKey}CovRN`]),
  cnT: getCovarianceValue(conjunction, [`${objectKey}CovTN`]),
  covRefFrame: "RTN" as const,
  crR: getCovarianceValue(conjunction, [`${objectKey}CovRR`]),
  ctR: getCovarianceValue(conjunction, [`${objectKey}CovRT`]),
  ctT: getCovarianceValue(conjunction, [`${objectKey}CovTT`]),
});

const buildObjectState = (
  conjunction: unknown,
  objectKey: ObjectKey,
  isPrimary: boolean,
): CDMObject | null => {
  const x = getNumericValue(conjunction, [`${objectKey}X`]);
  const y = getNumericValue(conjunction, [`${objectKey}Y`]);
  const z = getNumericValue(conjunction, [`${objectKey}Z`]);
  const xDot = getNumericValue(conjunction, [`${objectKey}Vx`]);
  const yDot = getNumericValue(conjunction, [`${objectKey}Vy`]);
  const zDot = getNumericValue(conjunction, [`${objectKey}Vz`]);

  if (
    x === undefined ||
    y === undefined ||
    z === undefined ||
    xDot === undefined ||
    yDot === undefined ||
    zDot === undefined
  ) {
    return null;
  }

  const source = getObjectSource(conjunction, objectKey);

  return {
    areaDrg: null,
    areaPc: getNumericValue(conjunction, [`${objectKey}AreaPc`]) ?? null,
    areaSrp: null,
    catalogName: source,
    cdAreaOverMass: 0,
    covarianceMatrix: getCovarianceMatrix(conjunction, objectKey),
    covarianceMethod: "CALCULATED",
    crAreaOverMass: null,
    ephemerisName: source,
    hbr: getNumericValue(conjunction, [`${objectKey}Hbr`]) ?? null,
    manoeuvrable: "N/A",
    mass: null,
    objectFix: {
      internationalDesignator: getStringValue(
        conjunction,
        [objectKey, "spaceObject", "cosparId"],
      ),
      objectDesignator: getObjectDesignator(conjunction, objectKey),
      objectName: getObjectName(
        conjunction,
        objectKey,
        getObjectRoleLabel(isPrimary ? "target" : "chaser"),
      ),
      objectType: null,
      operatorContactPosition: null,
      operatorEmail: null,
      operatorOrganization: null,
      operatorPhone: null,
    },
    orbitPeriod: null,
    primaryObject: isPrimary,
    refFrame: "GCRF",
    stateVector: {
      x,
      xDot,
      y,
      yDot,
      z,
      zDot,
    },
  };
};

const buildConjunctionCdm = (conjunction: unknown): IConjunction | null => {
  const primaryObject = buildObjectState(conjunction, "target", true);
  const secondaryObject = buildObjectState(conjunction, "chaser", false);

  if (!primaryObject || !secondaryObject) return null;

  const tca = toIsoDate(getRawValue(conjunction, ["timeOfCloseApproach"]));
  const creationDate = toIsoDate(
    getRawValue(conjunction, ["executionEpoch"]) ??
      getRawValue(conjunction, ["detectionTime"]),
  );

  return {
    autonomous: null,
    collisionProbabilityMethod: getStringValue(
      conjunction,
      ["collisionProbabilityMethod"],
      "",
    ),
    creationDate,
    messageFor: null,
    messageId: getStringValue(
      conjunction,
      ["conjunctionIdentifier"],
      getStringValue(conjunction, ["id"], "fallback-cdm"),
    ),
    missDistance: getNumberOrZero(conjunction, ["missDistance"]),
    objectStates: [primaryObject, secondaryObject],
    originator: getStringValue(conjunction, ["operator"], "FOCUSOC"),
    poc: getPositiveNumber(conjunction, [
      ["scaledCollisionProbability"],
      ["collisionProbability"],
      ["maximumCollisionProbability"],
    ]),
    primaryObject,
    relativePositionN: getNumberOrZero(conjunction, ["crossTrackDistance"]),
    relativePositionR: getNumberOrZero(conjunction, ["radialDistance"]),
    relativePositionT: getNumberOrZero(conjunction, ["alongTrackDistance"]),
    relativeSpeed: getNumberOrZero(conjunction, ["relativeSpeed"]),
    relativeVelocityN: getNumberOrZero(conjunction, ["crossTrackVelocity"]),
    relativeVelocityR: getNumberOrZero(conjunction, ["radialVelocity"]),
    relativeVelocityT: getNumberOrZero(conjunction, ["alongTrackVelocity"]),
    screenEntryTime: toIsoDate(getRawValue(conjunction, ["analysisTimeStart"])),
    screenExitTime: toIsoDate(getRawValue(conjunction, ["analysisTimeEnd"])),
    screenVolumeFrame: "RTN",
    screenVolumeShape: "ELLIPSOID",
    screenVolumeX: getNumberOrZero(conjunction, ["missDistanceThreshold"]),
    screenVolumeY: getNumberOrZero(conjunction, [
      "collisionProbabilityThreshold",
    ]),
    screenVolumeZ: getNumberOrZero(conjunction, [
      "maximumCollisionProbabilityThreshold",
    ]),
    secondaryObject,
    startScreenPeriod: toIsoDate(
      getRawValue(conjunction, ["analysisTimeStart"]),
    ),
    stopScreenPeriod: toIsoDate(getRawValue(conjunction, ["analysisTimeEnd"])),
    tca,
  };
};

const ConjunctionFallbackScene = ({ cdm }: { cdm: IConjunction }) => {
  const {
    addEntity,
    getCameraFrustum,
    isViewerLoaded,
    removeEntity,
    trackEntity,
    updateCurrentTime,
    updateViewerSimulationTime,
  } = useCesium();
  const { setExtend } = useExtend();
  const cdmEntity = useRef<Entity | null>(null);

  const tca = useMemo(() => JulianDate.fromIso8601(cdm.tca), [cdm.tca]);
  const primaryObject = useMemo(
    () => getCDMObjectAttributes(tca, cdm, 0),
    [cdm, tca],
  );
  const secondaryObject = useMemo(
    () => getCDMObjectAttributes(tca, cdm, 1),
    [cdm, tca],
  );
  const relativeVelocityPositions = useMemo(
    () =>
      getRelativeVelocityPositions(
        secondaryObject.position,
        primaryObject.velocity,
        secondaryObject.velocity,
      ),
    [primaryObject, secondaryObject],
  );
  const primaryOrbitMaterial = useMemo(
    () => rgbaToColor(vfColour2RGB("RED")).withAlpha(1),
    [],
  );
  const secondaryOrbitMaterial = useMemo(
    () => rgbaToColor(vfColour2RGB("GREEN")).withAlpha(1),
    [],
  );

  useEffect(() => {
    if (!isViewerLoaded) return undefined;

    if (cdmEntity.current) {
      removeEntity(cdmEntity.current);
    }

    const cameraEntity = getCameraBPPointAndPlaneEntity(
      primaryObject.position,
      primaryObject.velocity,
      secondaryObject.position,
      secondaryObject.velocity,
      tca,
      getCameraFrustum(),
    );
    cdmEntity.current = addEntity(cameraEntity);
    trackEntity(cdmEntity.current);

    const start = JulianDate.addSeconds(
      tca,
      -primaryObject.orbitPeriod / 2,
      new JulianDate(),
    );
    const stop = JulianDate.addSeconds(
      tca,
      primaryObject.orbitPeriod / 2,
      new JulianDate(),
    );

    updateViewerSimulationTime({
      currentTime: tca,
      multiplier: 0,
      onReady: () => {
        setExtend({
          end: JulianDate.toDate(stop).getTime(),
          start: JulianDate.toDate(start).getTime(),
        });
      },
      startTime: start,
      stopTime: stop,
    });
    updateCurrentTime(tca);

    return () => {
      if (cdmEntity.current) {
        removeEntity(cdmEntity.current);
      }
    };
  }, [
    addEntity,
    getCameraFrustum,
    isViewerLoaded,
    primaryObject,
    removeEntity,
    secondaryObject,
    setExtend,
    tca,
    trackEntity,
    updateCurrentTime,
    updateViewerSimulationTime,
  ]);

  return (
    <>
      <Orbit
        futureMaterial={primaryOrbitMaterial}
        id="fallback-primary"
        leadTime={primaryObject.orbitPeriod}
        name={primaryObject.name}
        orbitColor={vfColour2RGB("RED")}
        orbitPositions={primaryObject.position}
        showFutureOrbit
        trailTime={primaryObject.orbitPeriod / 2}
      />
      <Label positions={primaryObject.position} label={primaryObject.name} />
      <Vector
        availability={primaryObject.availability}
        name={primaryObject.name}
        positions={primaryObject.vector}
      />
      <Ellipsoid
        availability={primaryObject.availability}
        name={primaryObject.name}
        orientation={primaryObject.orientation}
        position={primaryObject.position}
        radii={primaryObject.radii}
      />

      <Orbit
        futureMaterial={secondaryOrbitMaterial}
        id="fallback-secondary"
        leadTime={secondaryObject.orbitPeriod}
        name={secondaryObject.name}
        orbitColor={vfColour2RGB("GREEN")}
        orbitPositions={secondaryObject.position}
        showFutureOrbit
        trailTime={secondaryObject.orbitPeriod / 2}
      />
      <Label positions={secondaryObject.position} label={secondaryObject.name} />
      <Ellipsoid
        availability={secondaryObject.availability}
        color={Color.GREEN}
        name={secondaryObject.name}
        orientation={secondaryObject.orientation}
        position={secondaryObject.position}
        radii={secondaryObject.radii}
      />
      <Vector
        availability={secondaryObject.availability}
        name={secondaryObject.name}
        positions={secondaryObject.vector}
      />
      <Vector
        availability={primaryObject.availability}
        color={Color.BLUE}
        name="relative_velocity"
        positions={relativeVelocityPositions}
      />
    </>
  );
};

export const ConjunctionFallback3DView = ({
  conjunction,
}: ConjunctionFallback3DViewProps) => {
  const fallbackCdm = useMemo(
    () => buildConjunctionCdm(conjunction),
    [conjunction],
  );

  if (!fallbackCdm) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm font-medium text-slate-500">
        No CDM available and state vector data is missing for this conjunction.
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full">
      <Visual3DWidget>
        <ConjunctionFallbackScene cdm={fallbackCdm} />
      </Visual3DWidget>
    </div>
  );
};
